import { FormProvider, useForm } from "react-hook-form"
import { graphql, useFragment, useLazyLoadQuery } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { DialogDescription } from "@/components/ui/dialog"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { DeBillingSection } from "@/modules/common/billing/components/billing-form/de/de-billing-section"
import { getDeBillingDefaultValues } from "@/modules/common/billing/components/billing-form/de/libs/getDeBillingDefaultValues"
import {
  DeBillingSchema,
  DeBillingSchemaType,
} from "@/modules/common/billing/components/billing-form/de/schema/de-billing-schema"
import { BillingDialogFooter } from "@/modules/patients/agenda/teleconsultation/appointment-action-menu/billing/billing-dialog-footer"
import { deBillingDialogContentBillingFragment$key } from "@/modules/patients/agenda/teleconsultation/appointment-action-menu/billing/de/__generated__/deBillingDialogContentBillingFragment.graphql"
import { deBillingDialogContentBillingQuery } from "@/modules/patients/agenda/teleconsultation/appointment-action-menu/billing/de/__generated__/deBillingDialogContentBillingQuery.graphql"
import { EditBillingAttributesInput } from "@/modules/patients/detail/conclude/billing-section/services/__generated__/useEditBillingAttributesMutation.graphql"
import { useEditBillingDialogMutation } from "@/modules/patients/detail/conclude/billing-section/services/useEditBillingAttributesMutation"

export const deBillingDialogContentBillingQueryDescriptor = graphql`
  query deBillingDialogContentBillingQuery($id: ID!) {
    appointmentById(id: $id) {
      ...deBillingDialogContentBillingFragment
    }
  }
`

const deBillingDialogContentBillingFragment = graphql`
  fragment deBillingDialogContentBillingFragment on Appointment {
    id
    service
    billingAttribute {
      id
      isNotBillable
      notBillableReason
      reason
      isVisualSymptomExamination
    }
  }
`

type DeBillingDialogContentProps = {
  isEditable: boolean
  appointmentId: string
  onCancel: () => void
  onSave: () => void
}

export const DeBillingDialogContent = ({
  isEditable,
  appointmentId,
  onCancel,
  onSave,
}: DeBillingDialogContentProps) => {
  const data = useLazyLoadQuery<deBillingDialogContentBillingQuery>(deBillingDialogContentBillingQueryDescriptor, {
    id: appointmentId,
  })

  const appointment = useFragment<deBillingDialogContentBillingFragment$key>(
    deBillingDialogContentBillingFragment,
    data.appointmentById,
  )

  if (!appointment) {
    throw new Error("Appointment not found")
  }

  const billingData = appointment.billingAttribute
  const isAdministrativeRequest = appointment.service === "ADMINISTRATIVE_REQUEST"

  const form = useForm<DeBillingSchemaType>({
    mode: "all",
    resolver: zodResolver(DeBillingSchema),
    defaultValues: getDeBillingDefaultValues(appointment, billingData),
  })

  const { handleSubmit } = form

  const [commitBillingDialogMutation, isFlight] = useEditBillingDialogMutation()

  const handleSave = async (billingData: DeBillingSchemaType) => {
    const { isNotBillable, reason, notBillableReason } = billingData

    const appointmentId = appointment.id

    let editBillingInput: EditBillingAttributesInput = { appointmentId, isNotBillable }

    if (isNotBillable) {
      editBillingInput = {
        ...editBillingInput,
        reason: notBillableReason === "OTHER_REASON" ? reason : null,
        notBillableReason,
      }
    } else {
      editBillingInput = {
        ...editBillingInput,
        isVisualSymptomExamination: billingData.isVisualSymptomExamination,
      }
    }

    commitBillingDialogMutation(editBillingInput, () => {
      onSave()
    })
    FaroInsightsWrapper.pushEvent("agenda_billing_information_save")
  }

  return (
    <FormProvider {...form}>
      <DialogDescription>
        <DeBillingSection disabled={!isEditable} isAdministrativeRequest={isAdministrativeRequest} />
      </DialogDescription>
      <BillingDialogFooter
        isEditable={isEditable}
        isLoading={isFlight}
        onSave={handleSubmit(handleSave)}
        onCancel={onCancel}
      />
    </FormProvider>
  )
}
